import type { ButtonHTMLAttributes } from 'react'
import { forwardRef } from 'react'
import { classNames } from '@/ui/utils/main'
import { Loader2 } from 'lucide-react'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive'
  size?: 'sm' | 'md' | 'lg'
  isLoading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      children,
      disabled,
      ...props
    },
    fwdRef,
  ) => {
    const buttonClasses = classNames(
      'btn inline-flex items-center justify-center gap-2 font-medium transition-colors',
      {
        'bg-primary text-primary-foreground hover:bg-primary/90': variant === 'primary',
        'bg-secondary text-secondary-foreground hover:bg-secondary/80': variant === 'secondary',
        'border border-input bg-background hover:bg-accent hover:text-accent-foreground':
          variant === 'outline',
        'hover:bg-accent hover:text-accent-foreground': variant === 'ghost',
        'bg-destructive text-destructive-foreground hover:bg-destructive/90':
          variant === 'destructive',
      },
      {
        'h-9 px-3 text-sm': size === 'sm',
        'h-10 px-4 py-2': size === 'md',
        'h-11 px-8 text-lg': size === 'lg',
      },
      {
        'opacity-50 cursor-not-allowed': disabled || isLoading,
      },
      className,
    )

    return (
      <button ref={fwdRef} className={buttonClasses} disabled={disabled || isLoading} {...props}>
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Loading...</span>
          </>
        ) : (
          <>
            {leftIcon && <span className="flex items-center">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="flex items-center">{rightIcon}</span>}
          </>
        )}
      </button>
    )
  },
)
Button.displayName = 'Button'

export { Button }
